import { useState } from 'react';
import { useSelector } from 'react-redux';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';

import { anchorDayToTZ, calendarDaysUntilInTZ, formatDate } from '#common/utils';

const DAY_MS = 24 * 60 * 60 * 1000;

export interface PendingItemUpdate {
  shipDate?: string;
  deadline?: string;
}

interface PendingItemStatusProps {
  confirmDeadline?: string | number | Date | null | undefined;
  orderCreatedDate?: string | number | Date | undefined;
  // salespeople see the status only
  canEdit: boolean;
  onSave: (update: PendingItemUpdate) => Promise<boolean>;
}

export default function PendingItemStatus(props: PendingItemStatusProps) {
  const {
    confirmDeadline, orderCreatedDate, canEdit, onSave,
  } = props;
  const timeZone = useSelector((s: { appConfig: { timeZone?: string } }) => s.appConfig.timeZone);
  const [mode, setMode] = useState<'shipDate' | 'deadline' | null>(null);
  const [value, setValue] = useState<Date | null>(null);

  let label;
  let color: 'warning' | 'error' | 'default' = 'warning';
  if (confirmDeadline) {
    const daysLeft = calendarDaysUntilInTZ(timeZone, confirmDeadline);
    const deadline = formatDate(confirmDeadline, '', { timeZone });
    if (new Date(confirmDeadline).getTime() > Date.now()) {
      const left = daysLeft > 0 ? `${daysLeft} ${daysLeft === 1 ? 'day' : 'days'} left` : 'due today';
      label = `Pending · confirm by ${deadline} (${left})`;
    } else {
      label = `Pending · deadline passed ${deadline}`;
      color = 'error';
    }
  } else {
    const days = orderCreatedDate
      ? Math.max(Math.floor((Date.now() - new Date(orderCreatedDate).getTime()) / DAY_MS), 0)
      : 0;
    label = `No deadline · pending ${days} ${days === 1 ? 'day' : 'days'}`;
    color = 'default';
  }

  const close = () => {
    setMode(null);
    setValue(null);
  };

  const handleSave = () => {
    if (!mode || !value || Number.isNaN(value.getTime())) {
      return;
    }
    const iso = anchorDayToTZ(timeZone, value).toISOString();
    void onSave(mode === 'shipDate' ? { shipDate: iso } : { deadline: iso }).then((ok) => {
      if (ok) {
        close();
      }
    });
  };

  return (
    <Box sx={{ mt: 1, displayPrint: 'none' }}>
      <Chip size="small" color={color} label={label} />
      {canEdit && (
        <Box sx={{ mt: 0.5 }}>
          <Button size="small" onClick={() => setMode('shipDate')}>Set ship date</Button>
          {!!confirmDeadline && (
            <Button size="small" onClick={() => setMode('deadline')}>Extend</Button>
          )}
        </Box>
      )}
      <Dialog open={!!mode} onClose={close}>
        <DialogTitle>{mode === 'shipDate' ? 'Set ship date' : 'Extend confirmation deadline'}</DialogTitle>
        <DialogContent sx={{ pt: 1 }}>
          <LocalizationProvider dateAdapter={AdapterDateFns}>
            <DatePicker
              label={mode === 'shipDate' ? 'Ship date' : 'New deadline'}
              value={value}
              disablePast={mode === 'deadline'}
              onChange={(d) => setValue(d)}
              slotProps={{ textField: { fullWidth: true, sx: { mt: 1 } } }}
            />
          </LocalizationProvider>
        </DialogContent>
        <DialogActions>
          <Button onClick={close}>Cancel</Button>
          <Button variant="contained" disabled={!value} onClick={handleSave}>Save</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
